import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const CLOSE_REASONS = [
  "Applied by Mistake",
  "Less Budget",
  "Looking Other Country",
  "Looking Work Permit",
  "B2B",
  "Other",
];

/**
 * CloseReasonDialog — captured whenever a lead is moved to "Closed"
 * (in either the Leads or Counselings section).
 * Props: open, onOpenChange, onConfirm({ reason, detail })
 */
export default function CloseReasonDialog({ open, onOpenChange, onConfirm }) {
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setDetail("");
    }
  }, [open]);

  const confirm = () => {
    if (!reason) return;
    onConfirm({ reason, detail: detail.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="close-reason-dialog">
        <DialogHeader>
          <DialogTitle>Why are you closing this lead?</DialogTitle>
          <DialogDescription>
            This helps Analytics surface the biggest reasons leads drop off.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="close-reason-select">
              <SelectValue placeholder="Select a reason" />
            </SelectTrigger>
            <SelectContent>
              {CLOSE_REASONS.map((r) => (
                <SelectItem key={r} value={r} data-testid={`close-reason-${r}`}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {reason === "Other" && (
            <Textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="Describe the reason…"
              rows={2}
              data-testid="close-reason-detail"
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="close-reason-cancel">
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={!reason}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="close-reason-confirm"
          >
            Close lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
